import { simpleParseStay } from '@/misc/stay/rawStayMapper'
import { documentsChunksByGeneratedDataId, mergeStayDocuments, overrideStayChunks, prefetchMcoStayDocuments, prefetchSmrStayDocuments, reuseStayChunks, reuseStayDocuments } from '@/misc/stayDocuments'
import { getFalseJustifications, getSmrFalseJustifications } from '@/misc/stayFalseJustifications'
import { getKeywordJustifications, getSmrSuggestionJustifications, getSuggestionJustifications } from '@/misc/stayJustifications'
import { getSmrStaySuggestions, getStaySuggestions } from '@/misc/staySuggestions'
import type {
  Commit,
  CurrentSearchLike,
  ListFalseJustificationsResponse,
  ListMcoStaySuggestionsResponse,
  ListOrphanPresentContentsJustificationsResponse,
  ListSmrStaySuggestionsResponse,
  ListStayDocumentsResponse,
  ListStayJustificationsResponse,
  PreviouslyDisplayedMcoStay,
  PreviouslyDisplayedSmrStay,
  StayDocumentEntries,
} from '@/misc/types'
import type { Chunk } from '@/stay-displayer/health-data/types'
import type { RawMcoStay } from '@/store/modules/stay/mco/types'
import type { RawSmrStay } from '@/store/modules/stay/smr/types'

/**
 * Merges the stay's documents in, runs the stay parsing and commits the result.
 * On failure, unblocks the UI instead of leaving it stuck loading, and reports the error via a toast.
 */
export async function refreshDisplayedMcoStay(
  stay: RawMcoStay,
  commit: Commit,
  currentSearch: unknown,
  appSettings: unknown,
  previousStay: PreviouslyDisplayedMcoStay | null = null,
  prefetchedDocumentsPromise: Promise<ListStayDocumentsResponse> | null = null,
  prefetchedJustificationsPromise: Promise<ListStayJustificationsResponse> | null = null,
  prefetchedKeywordJustificationsPromise: Promise<ListOrphanPresentContentsJustificationsResponse> | null = null,
  prefetchedSuggestionsPromise: Promise<ListMcoStaySuggestionsResponse> | null = null,
  prefetchedFalseJustificationsPromise: Promise<ListFalseJustificationsResponse> | null = null
): Promise<void> {
  commit('startRefreshingStayContent')

  const [suggestionJustifications, keywordJustifications, staySuggestions, falseJustifications] = await Promise.all([
    getSuggestionJustifications(stay, commit, previousStay, prefetchedJustificationsPromise, true),
    getKeywordJustifications(stay, currentSearch as CurrentSearchLike, commit, previousStay, prefetchedKeywordJustificationsPromise),
    getStaySuggestions(stay, commit, previousStay, prefetchedSuggestionsPromise, true),
    getFalseJustifications(stay, commit, previousStay, prefetchedFalseJustificationsPromise),
  ])

  let stayWithDocuments: RawMcoStay
  let chunksByGeneratedDataId: Map<string, Chunk[]>
  if (previousStay?.id === stay.id) {
    stayWithDocuments = { ...reuseStayDocuments(stay, previousStay), suggestionJustifications, keywordJustifications, staySuggestions, falseJustifications }
    chunksByGeneratedDataId = reuseStayChunks(previousStay)
  } else {
    let documents: ListStayDocumentsResponse
    try {
      documents = await (prefetchedDocumentsPromise ?? prefetchMcoStayDocuments('id', String(stay.id), currentSearch as CurrentSearchLike, commit, true))
    } catch {
      commit('toast/setError', 'Impossible de charger le contenu des documents du séjour')
      commit('updateDisplayedStay', simpleParseStay({ ...stay, suggestionJustifications, keywordJustifications, staySuggestions, falseJustifications }))
      commit('endRefreshingStayContent')
      return
    }

    stayWithDocuments = { ...mergeStayDocuments(stay, documents), suggestionJustifications, keywordJustifications, staySuggestions, falseJustifications }
    chunksByGeneratedDataId = documentsChunksByGeneratedDataId(documents)
  }

  // simpleParseStay returns a McoStay — only the reports/textualHealthEntries shape matters here, to
  // graft the backend chunks back on.
  const parsedStay = simpleParseStay({ ...stayWithDocuments }) as Pick<StayDocumentEntries, 'reports' | 'textualHealthEntries'>
  commit('updateDisplayedStay', overrideStayChunks(parsedStay, chunksByGeneratedDataId))
  commit('endRefreshingStayContent')
}

export async function refreshDisplayedSmrStay(
  stay: RawSmrStay,
  commit: Commit,
  currentSearch: unknown,
  appSettings: unknown,
  previousStay: PreviouslyDisplayedSmrStay | null = null,
  prefetchedDocumentsPromise: Promise<ListStayDocumentsResponse> | null = null,
  prefetchedJustificationsPromise: Promise<ListStayJustificationsResponse> | null = null,
  prefetchedSuggestionsPromise: Promise<ListSmrStaySuggestionsResponse> | null = null,
  prefetchedFalseJustificationsPromise: Promise<ListFalseJustificationsResponse> | null = null
): Promise<void> {
  const [suggestionJustifications, staySuggestions, falseJustifications] = await Promise.all([
    getSmrSuggestionJustifications(stay, commit, previousStay, prefetchedJustificationsPromise),
    getSmrStaySuggestions(stay, commit, previousStay, prefetchedSuggestionsPromise),
    getSmrFalseJustifications(stay, commit, previousStay, prefetchedFalseJustificationsPromise),
  ])

  let stayWithDocuments: RawSmrStay
  let chunksByGeneratedDataId: Map<string, Chunk[]>
  if (previousStay?.id === stay.id) {
    stayWithDocuments = { ...reuseStayDocuments(stay, previousStay), suggestionJustifications, staySuggestions, falseJustifications }
    chunksByGeneratedDataId = reuseStayChunks(previousStay)
  } else {
    let documents: ListStayDocumentsResponse
    try {
      documents = await (prefetchedDocumentsPromise ?? prefetchSmrStayDocuments('id', String(stay.id), currentSearch as CurrentSearchLike, commit))
    } catch {
      commit('toast/setError', 'Impossible de charger le contenu des documents du séjour')
      commit('smrStay/setCurrentStay', simpleParseStay({ ...stay, suggestionJustifications, staySuggestions, falseJustifications }))
      return
    }

    stayWithDocuments = { ...mergeStayDocuments(stay, documents), suggestionJustifications, staySuggestions, falseJustifications }
    chunksByGeneratedDataId = documentsChunksByGeneratedDataId(documents)
  }

  // simpleParseStay returns a SmrStay — only the reports/textualHealthEntries shape matters here, to
  // graft the backend chunks back on.
  const parsedStay = simpleParseStay({ ...stayWithDocuments }) as Pick<StayDocumentEntries, 'reports' | 'textualHealthEntries'>
  commit('smrStay/setCurrentStay', overrideStayChunks(parsedStay, chunksByGeneratedDataId))
}
